import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getInvoices, getARSummary, getClients } from '../services/api';
import { FileText, Plus, Search, X, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

const statusColor = (s) =>
  s === 'paid'     ? 'bg-green-50 text-green-700' :
  s === 'partial'  ? 'bg-blue-50 text-blue-700' :
  s === 'sent'     ? 'bg-amber-50 text-amber-700' :
  s === 'overdue'  ? 'bg-red-50 text-red-600' :
  s === 'cancelled'? 'bg-gray-50 text-gray-400' :
  'bg-purple-50 text-purple-700';

const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const GenerateInvoiceModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    client_id: '',
    month    : new Date().getMonth() + 1,
    year     : new Date().getFullYear()
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 100 }).then(r => r.data)
  });

  const mutation = useMutation({
    mutationFn: (data) => API.post('/invoices/generate', data),
    onSuccess: () => {
      toast.success('Invoice generated successfully!');
      queryClient.invalidateQueries(['invoices']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to generate invoice')
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      month: parseInt(form.month),
      year : parseInt(form.year)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Generate Invoice</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
            <select name="client_id" value={form.client_id} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select Client</option>
              {clientsData?.data?.map(c => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
              <select name="month" value={form.month} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {months.map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
              <select name="year" value={form.year} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 flex gap-2">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Invoice will be auto-generated from approved timesheets for the selected period.
              Make sure timesheets are approved before generating.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Generating...' : 'Generate Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Invoices = () => {
  const [status,  setStatus]  = useState('');
  const [month,   setMonth]   = useState('');
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', status, month, year],
    queryFn : () => getInvoices({ status, month, year, limit: 20 }).then(r => r.data)
  });

  const { data: arData } = useQuery({
    queryKey: ['ar-summary'],
    queryFn : () => getARSummary().then(r => r.data.data)
  });

  const sendMutation = useMutation({
    mutationFn: (id) => API.patch(`/invoices/${id}/send`),
    onSuccess: () => {
      toast.success('Invoice sent to client!');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send invoice')
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage client billing and payments</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} />
          Generate Invoice
        </button>
      </div>

      {/* AR Summary */}
      {arData && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Current',     amount: arData.current?.amount,       count: arData.current?.count,        color: 'text-blue-600' },
            { label: '0–30 days',   amount: arData.overdue_30?.amount,    count: arData.overdue_30?.count,     color: 'text-green-600' },
            { label: '30–60 days',  amount: arData.overdue_60?.amount,    count: arData.overdue_60?.count,     color: 'text-amber-600' },
            { label: '60+ days',    amount: arData.overdue_90_plus?.amount,count: arData.overdue_90_plus?.count,color: 'text-red-600' }
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-400 mb-1">{item.label}</p>
              <p className={`text-lg font-semibold ${item.color}`}>
                ₹{Number(item.amount || 0).toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-gray-400 mt-1">{item.count || 0} invoice(s)</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <select value={month} onChange={e => setMonth(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Months</option>
          {months.map((m, i) => (
            <option key={i} value={i + 1}>{m}</option>
          ))}
        </select>
        <select value={year} onChange={e => setYear(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value={2025}>2025</option>
          <option value={2026}>2026</option>
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Summary Bar */}
      {data?.summary && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex gap-8">
          <div>
            <p className="text-xs text-gray-400">Total Billed</p>
            <p className="text-lg font-semibold text-gray-900">₹{Number(data.summary.total_amount || 0).toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Collected</p>
            <p className="text-lg font-semibold text-green-600">₹{Number(data.summary.paid_amount || 0).toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Outstanding</p>
            <p className="text-lg font-semibold text-red-600">₹{Number(data.summary.outstanding_amount || 0).toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading invoices...</div>
        ) : data?.data?.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No invoices yet. Generate your first invoice!</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Invoice</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Client</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Period</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Amount</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Due Date</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.data?.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText size={14} className="text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{inv.invoice_no}</p>
                        <p className="text-xs text-gray-400">{inv._count?.invoice_line_items || 0} line items</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {inv.clients?.company_name}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {inv.month ? `${months[inv.month - 1]} ${inv.year}` : '—'}
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-gray-900">₹{Number(inv.total_amount).toLocaleString('en-IN')}</p>
                    {Number(inv.outstanding_amount) > 0 && (
                      <p className="text-xs text-red-500">₹{Number(inv.outstanding_amount).toLocaleString('en-IN')} due</p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {inv.due_date?.slice(0, 10)}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {inv.status === 'draft' && (
                      <button
                        onClick={() => sendMutation.mutate(inv.id)}
                        disabled={sendMutation.isPending}
                        className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        Send
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <GenerateInvoiceModal onClose={() => setShowAdd(false)} />}
    </div>
  );
};

export default Invoices;